import type { Ref } from 'react'

import { Button } from './button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { apiErrorMessage } from '@/lib/api-error-message'

export type RoleOption = { roleId: string; name: string; description?: string | null }

export type RolePickerProps = {
  roles: RoleOption[]
  isLoading?: boolean
  error?: Error | null
  onRetry?: () => void
  value: string | null
  onChange: (roleId: string) => void
  disabled?: boolean
  onBlur?: () => void
  ref?: Ref<HTMLButtonElement>
  id?: string
  className?: string
  'aria-label'?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

function RolePicker({
  roles,
  isLoading,
  error,
  onRetry,
  value,
  onChange,
  disabled,
  onBlur,
  ref,
  id,
  className,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: RolePickerProps) {
  if (isLoading) {
    return (
      <p
        data-slot="role-picker-loading"
        className="text-muted-foreground text-sm"
      >
        Loading roles…
      </p>
    )
  }

  if (error) {
    return (
      <div
        data-slot="role-picker-error"
        className="flex flex-col items-start gap-2"
      >
        <p role="alert" className="text-destructive text-sm">
          {apiErrorMessage(error, 'Couldn’t load roles. Try again.')}
        </p>
        {onRetry && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={onRetry}
          >
            Retry
          </Button>
        )}
      </div>
    )
  }

  if (roles.length === 0) {
    return (
      <p
        data-slot="role-picker-empty"
        className="text-muted-foreground text-sm"
      >
        No roles available to assign at this entity.
      </p>
    )
  }

  return (
    <Select
      value={value}
      onValueChange={(next) => {
        if (typeof next === 'string') onChange(next)
      }}
      disabled={disabled}
      items={Object.fromEntries(roles.map((role) => [role.roleId, role.name]))}
    >
      <SelectTrigger
        data-slot="role-picker"
        ref={ref}
        id={id}
        className={className}
        onBlur={onBlur}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
      >
        <SelectValue placeholder="Select a role…" />
      </SelectTrigger>
      <SelectContent>
        {roles.map((role) => (
          <SelectItem key={role.roleId} value={role.roleId} className="py-2">
            <span className="flex flex-col gap-0.5">
              <span className="font-medium">{role.name}</span>
              {role.description ? <span className="text-muted-foreground max-w-80 text-[11px] font-normal">{role.description}</span> : null}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export { RolePicker }
